import { createAsyncThunk, createSelector, createSlice, PayloadAction, SerializedError } from "@reduxjs/toolkit";
import { RootState } from "_shared/_stores/redux-store";
import EntityMemberAPIService from "_shared/entity-members/_api/entity-member-api-service";
import EntityMember from "_shared/entity-members/entity-member";
import EntityMemberSchema, { EntityMemberJSON } from "_shared/entity-members/entity-member-schema";
import Guid from "_shared/guids/guid";
import { AxiosError } from "axios";
import _ from "lodash";
import Session from "users/__session/session";

type EntityMemberStoreState = {
  byUserId: {
    entries: Record<string, EntityMemberJSON>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  byLegalEntityId: {
    entries: Record<string, EntityMemberJSON[]>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
};

const initialState: EntityMemberStoreState = {
  byUserId: {
    entries: {},
    loading: {},
    error: {},
  },
  byLegalEntityId: {
    entries: {},
    loading: {},
    error: {},
  }
};
export const populateEntityMembersByLegalEntityId = createAsyncThunk<
  EntityMemberJSON[],
  { session: Session; legalEntityId: Guid }
>(
  "entityMembers/getEntityMembersByLegalEntityId",
  async ({ session, legalEntityId }: { session: Session; legalEntityId: Guid }, thunkAPI) => {
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    const apiService = new EntityMemberAPIService({ session, abortController });
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      const response = await apiService.getMembersForLegalEntity(legalEntityId);
      response.forEach((memberJson) => {
        EntityMemberSchema.validateJSON(memberJson);
      });
      return response;
    } catch (error) {
      const apiError = error as AxiosError;
      return thunkAPI.rejectWithValue(apiError?.response?.data ?? error?.toString()
        ?? "An error occurred:" + JSON.stringify(error));
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  },
  {
    condition: ({ legalEntityId }: { session: Session; legalEntityId: Guid }, { getState }) => {
      const state = getState() as RootState;
      const isLoading = state.entityMembers.byLegalEntityId.loading[legalEntityId.value];
      //only dispatch if not already loading
      return !isLoading;
    }
  }
);

export const populateEntityMembersByUserIds = createAsyncThunk<
  EntityMemberJSON[],
  { session: Session; memberIds: Guid[] }
>(
  "entityMembers/getEntityMembersByUserIds",
  async ({ session, memberIds }, thunkAPI) => {
    if (!memberIds.length) return [];
    const store: { entityMembers: EntityMemberStoreState } = thunkAPI.getState() as { entityMembers: EntityMemberStoreState };
    const missingIds = _.uniqWith(
      memberIds.filter((id) => !store.entityMembers.byUserId.entries[id.value]),
      (a, b) => a.isEqualTo(b)
    );
    if (!missingIds.length) return [];
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    const apiService = new EntityMemberAPIService({ session, abortController });
    thunkAPI.signal.addEventListener("abort", onAbort);
    try {
      const response = await apiService.getMembersForLegalEntity(session.user!.legalEntityId);
      response.forEach((memberJson) => {
        EntityMemberSchema.validateJSON(memberJson);
      });
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.toString() ?? "An error occurred.");
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);

export const entityMembersSlice = createSlice({
  name: "entityMembers",
  initialState,
  reducers: {
    addEntityMember: (state, action: PayloadAction<EntityMember>) => {
      const id: Guid = action.payload.id;
      if (!id) return;
      state.byUserId.entries[id.value] = action.payload.toJSON();
      // Invalidate query cache
      state.byLegalEntityId = {
        entries: {},
        loading: {},
        error: {},
      };
    },
    removeEntityMember: (state, action: PayloadAction<EntityMember | Guid>) => {
      let memberId: Guid | undefined;
      if (action.payload instanceof Guid) {
        memberId = action.payload;
      } else {
        memberId = action.payload.id;
      }
      if (!memberId) {
        throw new Error("Entity member id not found");
      }
      delete state.byUserId.entries[memberId.value];
      state.byLegalEntityId = {
        entries: {},
        loading: {},
        error: {},
      };
    }
  },
  extraReducers: (builder) => {
    builder.addCase(populateEntityMembersByLegalEntityId.pending, (state, action) => {
      const entityId = action.meta.arg.legalEntityId.value;
      if (!entityId) return;
      state.byLegalEntityId.loading[entityId] = true;
      state.byLegalEntityId.error[entityId] = null;
    });
    builder.addCase(populateEntityMembersByLegalEntityId.fulfilled, (state, action) => {
      const entityId = action.meta.arg.legalEntityId.value;
      if (!entityId) return;
      state.byLegalEntityId.loading[entityId] = false;
      state.byLegalEntityId.entries[entityId] = action.payload;
    });

    builder.addCase(populateEntityMembersByLegalEntityId.rejected, (state, action) => {
      const entityId = action.meta.arg.legalEntityId.value;
      if (!entityId) return;
      state.byLegalEntityId.loading[entityId] = false;
      state.byLegalEntityId.error[entityId] = "Error fetching entity members: " + (action.payload as string ?? action.error.message);
    });

    builder.addCase(populateEntityMembersByUserIds.pending, (state, action) => {
      const memberIds = action.meta.arg.memberIds;
      if (!memberIds.length) return;
      for (const id of memberIds) {
        state.byUserId.loading[id.value] = true;
        state.byUserId.error[id.value] = null;
      }
    });

    builder.addCase(populateEntityMembersByUserIds.fulfilled, (state, action) => {
      const memberIds = action.meta.arg.memberIds;
      if (!memberIds.length) return;
      for (const memberJson of action.payload) {
        state.byUserId.entries[memberJson.userId] = memberJson;
      }
      for (const id of memberIds) {
        state.byUserId.loading[id.value] = false;
        if (!state.byUserId.entries[id.value]) {
          state.byUserId.error[id.value] = "Entity member not found";
        }
      }
    });

    builder.addCase(populateEntityMembersByUserIds.rejected, (state, action) => {
      const memberIds = action.meta.arg.memberIds;
      if (!memberIds.length) return;
      for (const id of memberIds) {
        state.byUserId.loading[id.value] = false;
        state.byUserId.error[id.value] = "Error fetching entity member info.";
      }
    });
  },
});

const selectEntityMembers =
  createSelector(
    [
      (state: RootState) => state.entityMembers.byLegalEntityId.entries,
      (_: RootState, entityId: Guid) => entityId.value,
    ],
    (entries, entityId) => {
      const members = entries[entityId];
      if (!members) return [];
      return EntityMember.fromJSONArray({ jsonArray: members }) || [];
    }
  );
export const getEntityMembers = (entityId: Guid) => (state: RootState) =>
  selectEntityMembers(state, entityId);

export const getEntityMembersIsLoading = (entityId: Guid) => (state: RootState) => {
  return state.entityMembers.byLegalEntityId.loading[entityId.value] ?? false;
};
export const getEntityMembersError =
  (entityId: Guid) =>
    (state: RootState): SerializedError | null => {
      const error = state.entityMembers.byLegalEntityId.error[entityId.value];
      if (error == null) return null;
      if (typeof error === 'string') {
        return { name: 'Error', message: error } as SerializedError;
      }
      return error;
    };
